/**
 * Friendly rejected-build screen. Build status `rejected` means the
 * pipeline ran cleanly but decided this business isn't a fit yet — most
 * commonly: outside AU/NZ, or a vertical we don't serve in Phase 2's
 * PRD Section 12 "Ready" tier.
 *
 * We surface `build.error` as the explanation copy (the API stores a
 * polite human-readable sentence in the `error` column for rejections;
 * fatal pipeline crashes flow through `status='error'` instead and
 * never land here).
 *
 * No Pipeline animation, no KB. Just the explanation and a CTA back to
 * the landing page.
 */

import { Link } from "react-router-dom";

import { Icon } from "./Icon.js";
import type { Build } from "./types.js";

interface RejectedProps {
  build: Build;
}

export function Rejected({ build }: RejectedProps) {
  const reason =
    build.error?.trim() ||
    "We couldn't fit this business to one of the verticals we serve yet.";

  return (
    <div
      className="fade-up"
      style={{ maxWidth: 560, margin: "40px auto", textAlign: "center" }}
    >
      <div
        className="live-badge"
        style={{
          background: "var(--warn-soft)",
          color: "var(--warn)",
          width: 84,
          height: 84,
        }}
      >
        <Icon name="info" size={42} stroke={2.4} />
      </div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 34,
          fontWeight: 800,
          letterSpacing: "-0.03em",
        }}
      >
        Not the right fit yet.
      </h1>
      <p
        style={{
          color: "var(--ink-2)",
          fontSize: 17,
          margin: "14px auto 24px",
          lineHeight: 1.55,
        }}
      >
        {reason}
      </p>
      <p
        style={{
          color: "var(--ink-3)",
          fontSize: 14.5,
          margin: "0 auto 32px",
          maxWidth: "44ch",
          lineHeight: 1.5,
        }}
      >
        Offsider's first release is for AU/NZ small service businesses —
        sparkies, plumbers, beauty, pet care and similar. We'll be widening the
        door as we grow. If you reckon we got this wrong, drop us a line at
        hello@offsider.com.au.
      </p>
      <Link
        to="/"
        className="btn btn-secondary btn-lg"
        data-testid="rejected-back-link"
      >
        <Icon name="arrow-left" size={16} />
        Open the landing page
      </Link>
    </div>
  );
}
